import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSim } from "@/context/SimContext";
import { Card, SectionTitle } from "@/components/ui-kit";
import { Play, Clock, CheckCircle2, AlertOctagon, Users } from "lucide-react";

const STATUS_TONE = {
  RUNNING: "text-emerald-400 border-emerald-800 bg-emerald-950/30",
  PENDING: "text-slate-400 border-slate-700 bg-slate-800/30",
  INTERRUPTED: "text-amber-400 border-amber-800 bg-amber-950/30",
  STOPPED: "text-red-400 border-red-800 bg-red-950/30",
  "AWAITING HUMAN DECISION": "text-amber-400 border-amber-800 bg-amber-950/30",
  "OPERATION PAUSED": "text-red-400 border-red-800 bg-red-950/30",
};

export default function Tasks() {
  const { eventTick } = useSim();
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = async () => {
      try { const { data } = await api.get("/tasks"); setData(data); } catch {}
    };
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [eventTick]);

  const c = data?.current;

  return (
    <div className="space-y-6 ngx-fade-in">
      <SectionTitle sub="Task states across the simulated industrial cell.">Task Management</SectionTitle>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat icon={Play} label="Current" value={c ? 1 : 0} tone="text-emerald-400" />
        <Stat icon={Clock} label="Pending" value={data?.pending?.length ?? 0} tone="text-slate-100" />
        <Stat icon={CheckCircle2} label="Completed" value={data?.completed_count ?? 0} tone="text-blue-400" />
        <Stat icon={Users} label="Interventions" value={data?.total_interventions ?? 0} tone="text-amber-400" />
      </div>

      {c && (
        <Card className="p-6" data-testid="current-task-card">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Current Task</span>
            <StatusBadge status={c.status} />
          </div>
          <div className="flex items-baseline gap-3">
            <span className="font-mono text-3xl text-slate-100">#{String(c.task_id).padStart(4, "0")}</span>
            <span className="font-display text-lg text-slate-300">{c.name}</span>
          </div>
          <div className="mt-5 flex items-center justify-between text-xs mb-1.5">
            <span className="uppercase tracking-wider text-slate-500">Progress</span>
            <span className="font-mono text-slate-300">{c.progress}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-slate-800 overflow-hidden">
            <div className="h-full bg-blue-500 transition-all" style={{ width: `${c.progress}%` }} />
          </div>
          <div className="mt-4 text-sm text-slate-400">Human interventions: <span className="font-mono text-slate-100">{c.interventions}</span></div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4 text-slate-300">
            <Clock className="h-4 w-4 text-blue-400" /><h3 className="font-display font-semibold">Pending Tasks</h3>
          </div>
          <div className="space-y-3" data-testid="pending-tasks">
            {(data?.pending || []).map((t) => <TaskRow key={t.task_id} t={t} />)}
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4 text-slate-300">
            <AlertOctagon className="h-4 w-4 text-amber-400" /><h3 className="font-display font-semibold">Interrupted / Stopped</h3>
          </div>
          <div className="space-y-3" data-testid="interrupted-tasks">
            {(data?.interrupted || []).length === 0 && (
              <div className="text-sm text-slate-500 py-6 text-center">No interrupted tasks.</div>
            )}
            {(data?.interrupted || []).map((t, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-md bg-[#050B14] border border-slate-800">
                <div>
                  <div className="font-mono text-sm text-slate-100">{t.event_no}</div>
                  <div className="text-xs text-slate-400">{t.name} · {t.time}</div>
                </div>
                <StatusBadge status={t.status} />
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function TaskRow({ t }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-md bg-[#050B14] border border-slate-800">
      <div>
        <div className="font-mono text-sm text-slate-100">#{String(t.task_id).padStart(4, "0")}</div>
        <div className="text-xs text-slate-400">{t.name}</div>
      </div>
      <StatusBadge status={t.status} />
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={`px-2.5 py-1 rounded-sm text-[11px] font-semibold uppercase tracking-wider border ${STATUS_TONE[status] || STATUS_TONE.PENDING}`}>
      {status}
    </span>
  );
}

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
        <Icon className="h-4 w-4 text-slate-500" />
      </div>
      <div className={`font-mono text-3xl font-medium mt-3 ${tone}`}>{value}</div>
    </Card>
  );
}
